import styled from '@emotion/styled';
import { ChangeEvent, MouseEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { warrantService } from '../../../api/services/warrant.service';
import { ORG_STATUS, OrganizationOverviewItemDto } from '../../../api/services/dto/user-managment-dto/organization-list-dto';
import { useAlert } from '../../shared/AlertProvider';
import StatsTile from '../../shared/stats-tiles/StatsTile';
import StatsTilesRow from '../../shared/stats-tiles/StatsTilesRow';
import { applicationService } from '../../../api/services/application.service';
import { buyerSeatService } from '../../../api/services/buyer-seat.service';
import OrganizationBreadcrumb from '../OrganizationBreadcrumb';
import Chip from '@mui/material/Chip';

interface OrgPaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

const OrgTablePaginationActions = ({ count, page, rowsPerPage, onPageChange }: OrgPaginationActionsProps) => (
  <Box sx={{ flexShrink: 0, ml: 2.5 }}>
    <IconButton
      onClick={(e) => onPageChange(e, page - 1)}
      disabled={page === 0}
      aria-label="previous page"
    >
      <KeyboardArrowLeft />
    </IconButton>
    <IconButton
      onClick={(e) => onPageChange(e, page + 1)}
      disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      aria-label="next page"
    >
      <KeyboardArrowRight />
    </IconButton>
  </Box>
);

const OrganizationList = () => {
  const { setAlert } = useAlert();
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState<OrganizationOverviewItemDto[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [prevCursor, setPrevCursor] = useState<string | undefined>(undefined);
  const [dealsCount, setDealsCount] = useState<number>(0);
  const [buyerSeatsCount, setBuyerSeatsCount] = useState<number>(0);
  const [activeCount, setActiveCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);

  const fetchOrganizations = (searchTerm?: string, action?: 'next' | 'prev', cursor?: string, limit?: number) => {
    warrantService
      .fetchOrganizationsList({ search: searchTerm || undefined, action, cursor, limit: limit ?? rowsPerPage })
      .then((res) => {
        setOrganizations(res.organizations);
        setNextCursor(res.meta.nextCursor);
        setPrevCursor(res.meta.prevCursor);
      })
      .catch((reason) =>
        setAlert({
          open: true,
          severity: 'error',
          title: 'Failed to fetch organizations',
          message: reason.message,
        })
      );
  };

  useEffect(() => {
    warrantService
      .countOrganizations()
      .then(setTotalCount)
      .catch(() => {});
  }, []);

  useEffect(() => {
    applicationService.getDealCount()
      .then(setDealsCount)
      .catch(() => {});
  }, []);

  useEffect(() => {
    buyerSeatService.getBuyerSeatCount()
      .then(setBuyerSeatsCount)
      .catch(() => {});
  }, []);

  useEffect(() => {
    warrantService.countActiveOrganizations()
      .then(setActiveCount)
      .catch(() => {});
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    setPage(0);
    setNextCursor(undefined);
    setPrevCursor(undefined);
    if (search) {
      timer = setTimeout(() => fetchOrganizations(search, undefined), 500);
    } else {
      fetchOrganizations(undefined, undefined);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [search]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > page) {
      fetchOrganizations(search, 'next', nextCursor);
    } else {
      fetchOrganizations(search, 'prev', prevCursor);
    }
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setNextCursor(undefined);
    setPrevCursor(undefined);
    fetchOrganizations(search, undefined, undefined, newRowsPerPage);
  };

  return (
    <ListPageWrapper>
    <OrganizationBreadcrumb items={[{ label: 'Organizations' }]} />
    
      <SearchAndActionsWrapper>
        <TextField
          placeholder="Search organizations"
          size="small"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          variant="outlined"
          sx={{
            minWidth: 325,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#F5F5F5',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {search && (
                  <IconButton
                    size="small"
                    onClick={() => handleSearchChange('')}
                    sx={{ p: 0.5, mr: 0.5 }}
                  >
                    <ClearIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </IconButton>
                )}
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />

        <ActionsWrapper>
          <Button
            variant="outlined"
            size="large"
            startIcon={<FileDownloadIcon />}
            onClick={() =>
              warrantService.exportOrganizations(totalCount).catch((reason) =>
                setAlert({
                  open: true,
                  severity: 'error',
                  title: 'Failed to export organizations',
                  message: reason.message,
                })
              )
            }
            sx={{
              backgroundColor: '#FFFFFF',
              color: 'text.primary',
              borderColor: '#0000001A',
              '&:hover': { backgroundColor: '#F5F5F5', borderColor: '#0000003A' },
            }}

          >
            Export
          </Button>
          <Button
            color="primary"
            variant="contained"
            size="large"
            onClick={() => navigate('/organization')}
          >
            Add Organization
          </Button>
        </ActionsWrapper>
      </SearchAndActionsWrapper>

      <StatsTilesRow>
        <StatsTile label="Total Organizations" value={totalCount} />
        <StatsTile label="Total Active Organizations" value={activeCount} />
        <StatsTile label="Total Deals" value={dealsCount} />
        <StatsTile label="Buyer Seats" value={buyerSeatsCount} />
      </StatsTilesRow>


      <TableContainer sx={{ backgroundColor: '#ffffff' }}>
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: '#F5F5F5', borderTop: '1px solid #0000001A' }}>
              <TableCell>Name</TableCell>
              <TableCell>Sales Owner</TableCell>
              <TableCell style={{ width: 100 }}>Deals</TableCell>
              <TableCell style={{ width: 120 }}>Buyer Seats</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell style={{ width: 50 }}>Status</TableCell>
              <TableCell style={{ width: 60 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organizations.length ? (
              organizations.map((org) => (
                <TableRow key={org.id}>
                  <TableCell>{org.name}</TableCell>
                  <TableCell>{org.salesOwner || '—'}</TableCell>
                  <TableCell>{org.dealsCount}</TableCell>
                  <TableCell>{org.buyerSeatsCount}</TableCell>
                  <TableCell>
                    {org.email || org.phone ? (
                      <ContactCell>
                        {org.email && <span>{org.email}</span>}
                        {org.phone && <span>{org.phone}</span>}
                      </ContactCell>
                    ) : '—'}
                  </TableCell>
                  <TableCell style={{ width: 100 }}>
                    <Chip
                      label={
                        org.status === ORG_STATUS.ACTIVE
                          ? 'Active'
                          : org.status === ORG_STATUS.INACTIVE
                            ? 'Inactive'
                            : 'Draft'
                      }
                      className={`table-chip ${org.status === ORG_STATUS.ACTIVE
                        ? 'organization-status-active'
                        : org.status === ORG_STATUS.INACTIVE
                          ? 'organization-status-inactive'
                          : 'organization-status-draft'
                        }`}
                    />
                  </TableCell>
                  <TableCell style={{ width: 60 }}>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/organization/list/${org.id}`)}
                      title="View details"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7}>
                  <NoMatches>No organizations found</NoMatches>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {organizations.length ? (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  colSpan={7}
                  count={nextCursor ? (page + 2) * rowsPerPage : (page + 1) * rowsPerPage}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{ native: true }}
                  onPageChange={(_e: MouseEvent<HTMLButtonElement> | null, newPage: number) =>
                    handlePageChange(newPage)
                  }
                  onRowsPerPageChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleRowsPerPageChange(parseInt(e.target.value, 10))
                  }
                  ActionsComponent={OrgTablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          ) : null}
        </Table>
      </TableContainer>
    </ListPageWrapper>
  );
};

export default OrganizationList;


const SearchAndActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 16px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NoMatches = styled.div`
  font-size: 16px;
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 20px;
`;

const ListPageWrapper = styled.div`
  width: 100%;
  max-width: 1440px;
  height: 100%;
`;

const ContactCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
`;
